import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore, useCartStore, useWishlistStore, useUIStore } from '../../store';
import { logoutUser } from '../../firebase/auth';
import { getCollections } from '../../firebase/database';
import toast from 'react-hot-toast';

const DEFAULT_COLLECTIONS = [
  { id:'living-room', name:'Living Room', subtitle:'Comfort Meets Style', slug:'living-room', image:'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=120&q=70' },
  { id:'office', name:'Office Collection', subtitle:'Work Better', slug:'office', image:'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=120&q=70' },
  { id:'kids', name:'Kids & Baby', subtitle:'Safe & Fun', slug:'kids', image:'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=120&q=70' },
  { id:'dining', name:'Dining Collection', subtitle:'Gather in Style', slug:'dining', image:'https://images.unsplash.com/photo-1449247709967-d4461a6a6103?w=120&q=70' },
  { id:'outdoor', name:'Outdoor & Garden', subtitle:'Al Fresco Living', slug:'outdoor', image:'https://images.unsplash.com/photo-1464146072230-91cabc968266?w=120&q=70' },
  { id:'premium', name:'Premium Collection', subtitle:'Luxury Redefined', slug:'premium', image:'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=120&q=70' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [collectionsMenu, setCollectionsMenu] = useState(false);
  const [collections, setCollections] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const collectionsRef = useRef(null);
  const { user, userRole, clearUser } = useAuthStore();
  const { getCount } = useCartStore();
  const { items: wishlist } = useWishlistStore();
  const { setCartOpen, searchOpen, setSearchOpen, mobileMenuOpen, setMobileMenuOpen } = useUIStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); setCollectionsMenu(false); }, [location.pathname]);

  useEffect(() => {
    getCollections().then(data => setCollections(data.length > 0 ? data : DEFAULT_COLLECTIONS))
      .catch(() => setCollections(DEFAULT_COLLECTIONS));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (collectionsRef.current && !collectionsRef.current.contains(e.target)) setCollectionsMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => { await logoutUser(); clearUser(); toast.success('Logged out'); navigate('/'); };
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) { navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`); setSearchOpen(false); setSearchQuery(''); }
  };
  const cartCount = getCount();

  return (
    <>
      {/* Announcement */}
      <div className="bg-[#0f2347] text-white py-2 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap inline-block text-xs tracking-widest font-semibold">
          {['FREE DELIVERY ON ORDERS OVER PKR 5,000',"PAKISTAN'S #1 SMART SEATING BRAND",'1-YEAR WARRANTY','SHOP NOW & SAVE UP TO 40%'].map((t,i)=>(
            <span key={i} className="mr-14 inline-flex items-center gap-2">
              <span className="w-1 h-1 bg-[#e63946] rounded-full"/>{t}
            </span>
          ))}
          {['FREE DELIVERY ON ORDERS OVER PKR 5,000',"PAKISTAN'S #1 SMART SEATING BRAND"].map((t,i)=>(
            <span key={i+'b'} className="mr-14 inline-flex items-center gap-2">
              <span className="w-1 h-1 bg-[#e63946] rounded-full"/>{t}
            </span>
          ))}
        </div>
      </div>

      {/* Main Nav */}
      <motion.nav className={`sticky top-0 z-40 transition-all duration-500 ${scrolled?'bg-white/95 backdrop-blur-xl shadow-lg':'bg-white border-b border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-[68px]">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 group">
              <img src="/logo.png" alt="SitSmart" className="h-12 w-auto object-contain group-hover:opacity-90 transition-opacity"/>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-7">
              {/* Home */}
              <Link to="/" className={`text-sm font-semibold tracking-wide transition-colors relative group ${location.pathname==='/'?'text-[#e63946]':'text-gray-700 hover:text-[#1a3a6e]'}`}>
                Home
                <span className={`absolute -bottom-0.5 left-0 h-0.5 bg-[#e63946] transition-all duration-300 ${location.pathname==='/'?'w-full':'w-0 group-hover:w-full'}`}/>
              </Link>
              {/* Shop */}
              <Link to="/shop" className={`text-sm font-semibold tracking-wide transition-colors relative group ${location.pathname==='/shop'?'text-[#e63946]':'text-gray-700 hover:text-[#1a3a6e]'}`}>
                Shop
                <span className={`absolute -bottom-0.5 left-0 h-0.5 bg-[#e63946] transition-all duration-300 ${location.pathname==='/shop'?'w-full':'w-0 group-hover:w-full'}`}/>
              </Link>
              {/* Collections Dropdown */}
              <div ref={collectionsRef} className="relative">
                <button onClick={() => setCollectionsMenu(!collectionsMenu)}
                  className={`flex items-center gap-1.5 text-sm font-semibold tracking-wide transition-colors relative group ${location.pathname.startsWith('/collections')?'text-[#e63946]':'text-gray-700 hover:text-[#1a3a6e]'}`}>
                  Collections
                  <motion.svg animate={{ rotate: collectionsMenu ? 180 : 0 }} transition={{ duration: 0.25 }}
                    className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
                  </motion.svg>
                  <span className={`absolute -bottom-0.5 left-0 h-0.5 bg-[#e63946] transition-all duration-300 ${location.pathname.startsWith('/collections')?'w-full':'w-0 group-hover:w-full'}`}/>
                </button>

                <AnimatePresence>
                  {collectionsMenu && (
                    <motion.div initial={{ opacity:0, y:12, scale:0.97 }} animate={{ opacity:1, y:0, scale:1 }}
                      exit={{ opacity:0, y:8, scale:0.97 }} transition={{ duration:0.2 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[500px] bg-white shadow-2xl border border-gray-100 z-50 overflow-hidden">
                      {/* Header */}
                      <div className="bg-gradient-to-r from-[#0f2347] to-[#1a3a6e] px-5 py-4">
                        <p className="text-white font-display font-bold text-base">Our Collections</p>
                        <p className="text-white/60 text-xs mt-0.5">Curated furniture for every room</p>
                      </div>
                      {/* Grid */}
                      <div className="grid grid-cols-2 gap-0 p-2">
                        {collections.map((col, i) => (
                          <motion.div key={col.id} initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:i*0.04 }}>
                            <Link to={`/collections/${col.slug||col.id}`} onClick={() => setCollectionsMenu(false)}
                              className="flex items-center gap-3 p-3 hover:bg-[#1a3a6e]/5 transition-colors group/item rounded-xl">
                              {/* Image or SVG icon */}
                              {col.image ? (
                                <div className="w-12 h-12 overflow-hidden flex-shrink-0 rounded-lg">
                                  <img src={col.image} alt={col.name} className="w-full h-full object-cover group-hover/item:scale-110 transition-transform duration-500"/>
                                </div>
                              ) : (
                                <div className="w-12 h-12 bg-[#1a3a6e]/10 flex items-center justify-center flex-shrink-0 rounded-lg">
                                  <svg className="w-6 h-6 text-[#1a3a6e]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 01-1.125-1.125v-3.75zM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-8.25zM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-2.25z"/>
                                  </svg>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 text-sm group-hover/item:text-[#1a3a6e] transition-colors">{col.name}</p>
                                <p className="text-gray-400 text-xs truncate">{col.subtitle||col.description||'Explore collection'}</p>
                              </div>
                              <svg className="w-3.5 h-3.5 text-gray-300 group-hover/item:text-[#e63946] group-hover/item:translate-x-0.5 transition-all flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                              </svg>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                      {/* View All */}
                      <div className="border-t border-gray-100 p-3">
                        <Link to="/collections" onClick={() => setCollectionsMenu(false)}
                          className="flex items-center justify-center gap-2 py-2.5 text-sm font-bold text-[#1a3a6e] hover:bg-[#1a3a6e] hover:text-white transition-all duration-300 group/all rounded-lg">
                          View All Collections
                          <svg className="w-4 h-4 group-hover/all:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {/* About */}
              <Link to="/about" className={`text-sm font-semibold tracking-wide transition-colors relative group ${location.pathname==='/about'?'text-[#e63946]':'text-gray-700 hover:text-[#1a3a6e]'}`}>
                About
                <span className={`absolute -bottom-0.5 left-0 h-0.5 bg-[#e63946] transition-all duration-300 ${location.pathname==='/about'?'w-full':'w-0 group-hover:w-full'}`}/>
              </Link>
              {/* Contact */}
              <Link to="/contact" className={`text-sm font-semibold tracking-wide transition-colors relative group ${location.pathname==='/contact'?'text-[#e63946]':'text-gray-700 hover:text-[#1a3a6e]'}`}>
                Contact
                <span className={`absolute -bottom-0.5 left-0 h-0.5 bg-[#e63946] transition-all duration-300 ${location.pathname==='/contact'?'w-full':'w-0 group-hover:w-full'}`}/>
              </Link>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1.5">
              <button onClick={() => setSearchOpen(!searchOpen)} className="p-2 text-gray-600 hover:text-[#1a3a6e] hover:bg-gray-100 rounded-full transition-all">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              </button>
              <Link to="/wishlist" className="p-2 text-gray-600 hover:text-[#e63946] hover:bg-gray-100 rounded-full transition-all relative">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
                {wishlist.length > 0 && <span className="absolute top-1 right-1 bg-[#e63946] text-white text-[9px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{wishlist.length}</span>}
              </Link>
              <button onClick={() => setCartOpen(true)} className="p-2 text-gray-600 hover:text-[#1a3a6e] hover:bg-gray-100 rounded-full transition-all relative">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/></svg>
                {cartCount > 0 && <motion.span initial={{scale:0}} animate={{scale:1}} className="absolute top-1 right-1 bg-[#1a3a6e] text-white text-[9px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{cartCount}</motion.span>}
              </button>

              {user ? (
                <div className="relative ml-1">
                  <button onClick={() => setUserMenu(!userMenu)} className="flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-full border border-gray-200 hover:border-[#1a3a6e] transition-all">
                    <div className="w-6 h-6 bg-gradient-to-br from-[#1a3a6e] to-[#e63946] rounded-full flex items-center justify-center text-white text-[10px] font-black">{user.displayName?.[0]?.toUpperCase()||'U'}</div>
                    <svg className={`w-3 h-3 text-gray-400 transition-transform ${userMenu?'rotate-180':''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                  </button>
                  <AnimatePresence>
                    {userMenu && (
                      <motion.div initial={{opacity:0,y:8,scale:0.95}} animate={{opacity:1,y:0,scale:1}} exit={{opacity:0,y:6,scale:0.96}}
                        className="absolute right-0 top-full mt-2 w-52 bg-white shadow-2xl border border-gray-100 z-50 overflow-hidden">
                        <div className="p-3.5 border-b border-gray-100 bg-gradient-to-r from-[#1a3a6e]/5 to-[#e63946]/5">
                          <p className="font-bold text-sm text-gray-900 truncate">{user.displayName||'User'}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                        <div className="py-1">
                          {userRole==='admin'?(
                            <Link to="/admin" onClick={()=>setUserMenu(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                              <svg className="w-4 h-4 text-[#1a3a6e]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                              Admin Panel
                            </Link>
                          ):(
                            <>
                              <Link to="/account" onClick={()=>setUserMenu(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                <svg className="w-4 h-4 text-[#1a3a6e]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                                My Account
                              </Link>
                              <Link to="/account/orders" onClick={()=>setUserMenu(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                <svg className="w-4 h-4 text-[#1a3a6e]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
                                My Orders
                              </Link>
                            </>
                          )}
                          <button onClick={handleLogout} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                            Logout
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link to="/login" className="hidden sm:flex ml-1 items-center gap-1.5 bg-[#1a3a6e] text-white px-4 py-2.5 text-sm font-bold hover:bg-[#e63946] transition-all duration-300">
                  Sign In
                </Link>
              )}
              <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-2 text-gray-600 ml-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/></svg>
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="border-t border-gray-100 overflow-hidden bg-white">
              <div className="max-w-2xl mx-auto px-4 py-3">
                <form onSubmit={handleSearch} className="flex border-2 border-[#1a3a6e] focus-within:border-[#e63946] transition-colors">
                  <input autoFocus type="text" placeholder="Search chairs, tables, sofas..."
                    value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}
                    className="flex-1 px-4 py-3 text-sm outline-none bg-transparent"/>
                  <button type="submit" className="px-5 py-3 bg-[#1a3a6e] text-white hover:bg-[#e63946] transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Mobile Drawer — slides in from LEFT, proper off-canvas pattern */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/60 z-[60]"
            />
            {/* Drawer */}
            <motion.div
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="lg:hidden fixed inset-y-0 left-0 w-[280px] max-w-[85vw] bg-white z-[70] flex flex-col shadow-2xl"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <img src="/logo.png" alt="SitSmart" className="h-10 w-auto object-contain" />
                <button onClick={() => setMobileMenuOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>

              {/* Nav Links */}
              <div className="flex-1 overflow-y-auto py-4 px-3">
                <div className="space-y-1 mb-4">
                  {[{label:'Home',to:'/'},{label:'Shop',to:'/shop'},{label:'About',to:'/about'},{label:'Contact',to:'/contact'},{label:'FAQs',to:'/faqs'}].map(({label,to})=>(
                    <Link key={to} to={to} onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-colors rounded-xl ${location.pathname===to?'bg-[#1a3a6e] text-white':'text-gray-700 hover:bg-gray-50'}`}>
                      {label}
                    </Link>
                  ))}
                </div>

                {/* Collections sub-section */}
                {collections.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-4 mb-2">Collections</p>
                    <div className="space-y-0.5">
                      {collections.map(col=>(
                        <Link key={col.id} to={`/collections/${col.slug||col.id}`} onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:text-[#1a3a6e] hover:bg-gray-50 rounded-xl transition-colors">
                          <span className="w-1.5 h-1.5 bg-[#e63946] rounded-full flex-shrink-0"/>
                          {col.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Drawer Footer */}
              <div className="border-t border-gray-100 p-4">
                {user ? (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#1a3a6e] rounded-full flex items-center justify-center text-white font-black text-sm flex-shrink-0">
                      {user.displayName?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{user.displayName || 'Account'}</p>
                      <Link to="/account" onClick={() => setMobileMenuOpen(false)} className="text-xs text-[#1a3a6e] font-semibold">View Account</Link>
                    </div>
                  </div>
                ) : (
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center w-full py-3 bg-[#e63946] text-white font-bold text-sm rounded-xl">
                    Sign In / Register
                  </Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
export default Navbar;
